// Firestore functions used by the Investigation tool.

import { v4 as uuidv4 } from "uuid";
import {
  doc,
  runTransaction,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  Firestore,
  collection,
} from "firebase/firestore";
import {
  FirebaseStorage,
  ref as storageRefFn,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

export type UploadImageInput = {
  imageUrl: string;
  imageDescription?: string;
  fileName?: string;
  contentType?: string;
};

export async function uploadStepAttachments(params: {
  db: Firestore;
  storage: FirebaseStorage;
  orgId: string;
  busId: string;
  step: number;
  images: UploadImageInput[];
}) {
  const { db, storage, orgId, busId, step, images } = params;
  if (!images?.length) return;

  const ids = images.map(() => uuidv4());

  await Promise.all(
    images.map(async (img, i) => {
      const uuid = ids[i];
      const sref = storageRefFn(storage, uuid);
      const blob = await (await fetch(img.imageUrl)).blob();
      await uploadBytes(sref, blob);
    })
  );

  await runTransaction(db, async (tx) => {
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const uuid = ids[i];
      const sref = storageRefFn(storage, uuid);
      const fullUrl = `gs://${sref.bucket}/${sref.fullPath}`;
      const downloadUrl = await getDownloadURL(sref);

      const imageData = {
        uuid,
        label: img.imageDescription,
        url: fullUrl,
        downloadUrl,
        storageRefPath: uuid,
        step,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        fileName: img.fileName,
        contentType: img.contentType,
      };

      tx.update(doc(db, `organizations/${orgId}/businesses/${busId}`), {
        attachments: arrayUnion(uuid),
      });

      tx.set(
        doc(db, `organizations/${orgId}/businesses/${busId}/attachments`, uuid),
        imageData
      );
    }
  });
}

// Deletes attachment in DB
export async function deleteAttachment(params: {
  db: Firestore;
  storage: FirebaseStorage;
  orgId: string;
  busId: string;
  uuid: string;
  storageRefPath?: string;
}) {
  const { db, storage, orgId, busId, uuid, storageRefPath } = params;
  const storageKey = storageRefPath ?? uuid;

  await deleteObject(storageRefFn(storage, storageKey));
  await runTransaction(db, async (tx) => {
    tx.delete(doc(db, `organizations/${orgId}/businesses/${busId}/attachments`, uuid));
    tx.update(doc(db, `organizations/${orgId}/businesses/${busId}`), {
      attachments: arrayRemove(uuid),
    });
  });
}

// Save investigation
export async function saveInvestigationToFirestore(params: {
  db: Firestore;
  orgId: string;
  busId: string;
  investigationId?: string;
  business: { id?: string; name?: string; category?: string; status?: string };
  user: { id?: string; email?: string; displayName?: string };
  steps: Array<{ label: string }>;
  answers: Record<number, Record<string, boolean | string>>;
}) {
  const {
    db,
    orgId,
    busId,
    investigationId = "latest",
    business,
    user,
    steps,
    answers,
  } = params;

  const payload = {
    businessId: business?.id ?? null,
    businessName: business?.name ?? null,
    category: business?.category ?? null,
    status: business?.status ?? null,
    steps: steps.map((s) => ({ label: s.label })),
    answers,
    updatedAt: serverTimestamp(),
    createdBy: {
      id: user?.id ?? null,
      email: user?.email ?? null,
      displayName: user?.displayName ?? null,
    },
  };

  const ref = doc(
    db,
    `organizations/${orgId}/businesses/${busId}/investigations/${investigationId}`
  );

  await runTransaction(db, async (tx) => {
    tx.set(ref, payload, { merge: true });
  });
}
